import { CustomerType } from '@myre/app-user-generated/types/CustomerType';
import { isProduction, StaticEnv } from '@myre/common-conf';
import { MyreApp } from '@myre/common-core';
import { type LangLocale } from '@myre/common-i18n';
import { Variant } from '@myre/domain-i18n-core';
import { loadI18n } from '@myre/domain-i18n-loader';
import { MYRE_I18N_SCOPE } from '@myre/ui-primitive-core';

import { inject, InjectionToken } from '@angular/core';
import {
  type TranslateLoader,
  type TranslationObject,
} from '@ngx-translate/core';
import { merge } from 'lodash-es';
import { from, map, zip, type Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CUSTOMER_TYPE, MYRE_APP } from './services/auth/auth.service';

export const WORDINGS = new InjectionToken('Wordings');

export class MyreTranslateLoader implements TranslateLoader {
  private _customerType = inject(CUSTOMER_TYPE, {
    optional: true,
  });

  private _myreApp = inject(MYRE_APP, {
    optional: true,
  });

  private _myreI18nScope = inject(MYRE_I18N_SCOPE, {
    optional: true,
  });

  private _wordings: Record<string, unknown> = {};

  public get wordings(): Record<string, unknown> {
    return this._wordings;
  }

  public getVariant(): Variant {
    switch (this._myreApp) {
      case MyreApp.AM: {
        switch (this._customerType) {
          case CustomerType.Tenant: {
            return Variant.Tenant;
          }
          case CustomerType.VAD: {
            return Variant.Vad;
          }
        }

        break;
      }
    }

    return null;
  }

  public getTranslation(lang: LangLocale): Observable<TranslationObject> {
    const staticEnv = isProduction()
      ? StaticEnv.Production
      : StaticEnv.Development;

    const scopes = Array.isArray(this._myreI18nScope)
      ? this._myreI18nScope
      : [this._myreI18nScope];
    const loaders = scopes.map(scope => {
      return from(
        loadI18n(staticEnv, scope, lang, {
          variant: this.getVariant(),
          withFrontendLibs: true,
        }),
      );
    });

    return zip(loaders)
      .pipe(
        map((responses: TranslationObject[]) => {
          return responses.reduce((result, scope: TranslationObject) => {
            return merge(result, scope);
          }, {} as TranslationObject);
        }),
      )
      .pipe(
        tap(wordings => {
          Object.assign(this._wordings, wordings);
        }),
      );
  }
}
